/**
 * Storage admin tab — orchestration shell (decomposed Phase 6).
 *
 * Super-admin view of all uploaded files: per-org usage + quotas, live
 * bucket-stats verification (Tigris ground-truth vs DB drift), and a
 * filterable file browser with single-file delete + orphan cleanup.
 *
 * Data/state lives in useStorageAdmin; sections are in pages/settings/storage/*.
 */
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Loader2 } from 'lucide-react';
import { formatBytes } from '../../utils/uploadImage';
import { useStorageAdmin } from '../../hooks/useStorageAdmin';
import { ContentTypeBar } from './storage/ContentTypeBar';
import { BucketStatsPanel } from './storage/BucketStatsPanel';
import { BucketSweepPanel } from './storage/BucketSweepPanel';
import { OrgUsageTable } from './storage/OrgUsageTable';
import { FileBrowser } from './storage/FileBrowser';

export default function StorageFilesTab() {
  const s = useStorageAdmin();

  if (s.loading) {
    return <div className="rounded-lg border p-10 text-center text-muted-foreground">Loading storage…</div>;
  }

  return (
    <div className="space-y-6 pb-12" data-testid="storage-files-tab">
      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-lg border p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Total used</div>
          <div className="text-2xl font-display font-bold tabular-nums mt-1" data-testid="storage-total-bytes">{formatBytes(s.grandTotal.bytes)}</div>
          <div className="text-xs text-muted-foreground mt-1">{s.grandTotal.files.toLocaleString()} files across {s.usage.length} orgs</div>
        </div>
        {s.myOrg && (
          <div className="rounded-lg border p-5 md:col-span-2">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">{s.myOrg.org_name} — by content type</div>
            <ContentTypeBar rows={s.myOrg.by_content_type || []} total={s.myOrg.total_bytes || 1} />
          </div>
        )}
      </div>

      {/* Live bucket stats — super_admin only. Ground-truth read of the
          bucket itself; the DB-vs-bucket drift line is the headline. */}
      <BucketStatsPanel
        isSuperAdmin={s.isSuperAdmin}
        bucketStats={s.bucketStats}
        bucketStatsLoading={s.bucketStatsLoading}
        onScan={s.loadBucketStats}
      />

      {/* Unreferenced-objects report — super_admin only, READ-ONLY. The
          destructive sweep was removed (May 2026); nothing auto-deletes. */}
      <BucketSweepPanel
        isSuperAdmin={s.isSuperAdmin}
        sweepPreview={s.sweepPreview}
        sweepLoading={s.sweepLoading}
        onPreview={s.runSweepPreview}
      />

      {/* Per-org usage table */}
      <OrgUsageTable
        usage={s.usage}
        isSuperAdmin={s.isSuperAdmin}
        onRefresh={s.loadUsage}
        onQuotaSave={s.handleQuotaSave}
      />

      {/* File browser */}
      <FileBrowser
        files={s.files}
        filesTotal={s.filesTotal}
        usage={s.usage}
        allTypes={s.allTypes}
        canDelete={s.canDelete}
        orgFilter={s.orgFilter}
        setOrgFilter={s.setOrgFilter}
        typeFilter={s.typeFilter}
        setTypeFilter={s.setTypeFilter}
        search={s.search}
        setSearch={s.setSearch}
        orphansOnly={s.orphansOnly}
        setOrphansOnly={s.setOrphansOnly}
        skip={s.skip}
        setSkip={s.setSkip}
        onSearch={s.handleSearch}
        onCleanupClick={() => s.setOrphanConfirm(true)}
        onDeleteClick={s.setPendingDelete}
      />

      {/* Delete confirm */}
      <AlertDialog open={!!s.pendingDelete} onOpenChange={(o) => !o && !s.busy && s.setPendingDelete(null)}>
        <AlertDialogContent data-testid="storage-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this file from storage?</AlertDialogTitle>
            <AlertDialogDescription>
              This wipes <span className="font-semibold text-foreground">"{s.pendingDelete?.filename}"</span> ({formatBytes(s.pendingDelete?.size_bytes || 0)}) from Tigris and the database. <strong>This cannot be undone.</strong>
              {s.pendingDelete?.module_id && (
                <p className="mt-2 text-amber-700">⚠ This file is referenced by a module — slides may stop rendering.</p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={s.busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={s.handleDelete} disabled={s.busy} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="storage-delete-confirm-btn">
              {s.busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Orphan cleanup confirm */}
      <AlertDialog open={s.orphanConfirm} onOpenChange={(o) => !s.busy && s.setOrphanConfirm(o)}>
        <AlertDialogContent data-testid="storage-cleanup-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Clean up orphan files?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes every uploaded file whose owning module has been deleted. Bytes are reclaimed from Tigris and the DB row is removed. Cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={s.busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={s.handleOrphanCleanup} disabled={s.busy} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="storage-cleanup-confirm-btn">
              {s.busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Clean orphans
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
